import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, X, ChevronRight, Check, Loader2, Lightbulb, MessageCircle, Send, ArrowLeft,
} from 'lucide-react';
import api from '../../lib/api';

const STORAGE_KEY = 'corpcraft_olivia_collapsed_v1';

/**
 * Olivia — guided onboarding panel.
 *
 * Pulls the user's next step from /api/olivia/state, lists all steps with
 * checkmarks, and offers a "Continue" action that navigates them to the right
 * page. Auto-hides once the user is fully onboarded (is_complete=true) unless
 * they manually re-open it.
 */
export default function OliviaPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
  const [view, setView] = useState('guide'); // 'guide' | 'chat'
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', text: "Hi! I'm Olivia. Ask me anything about your structure, IP, fundability, or what to do next." },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatScrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [stateRes, stepsRes] = await Promise.all([
        api.get('/olivia/state'),
        api.get('/olivia/steps'),
      ]);
      setData(stateRes.data);
      setSteps(stepsRes.data.steps || []);
      // First-load only: auto-collapse for fully-onboarded users so the panel
      // doesn't take over the dashboard.
      if (stateRes.data?.progress?.percent === 100 && localStorage.getItem(STORAGE_KEY) === null) {
        localStorage.setItem(STORAGE_KEY, 'true');
        setCollapsed(true);
      }
    } catch (err) {
      // Olivia is non-critical — fail silently
      console.warn('Olivia state load failed:', err?.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  };

  const sendChatMessage = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatMessages((prev) => [...prev, { role: 'user', text }]);
    setChatInput('');
    setChatSending(true);
    try {
      const res = await api.post('/olivia/chat', { message: text });
      setChatMessages((prev) => [...prev, { role: 'assistant', text: res.data.response }]);
    } catch (err) {
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', text: err.response?.data?.detail || 'I had trouble responding. Please try again.', error: true },
      ]);
    } finally {
      setChatSending(false);
    }
  };

  // Auto-scroll chat
  useEffect(() => {
    if (view === 'chat' && chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages, view]);

  if (loading || !data) return null;

  // Show the bubble when collapsed; show the full panel when expanded.
  // (Auto-collapse on first load if 100% — handled in load())
  const showFull = !collapsed;

  if (!showFull) {
    return (
      <button
        onClick={toggleCollapsed}
        data-testid="olivia-bubble"
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-2xl shadow-blue-500/30 hover:scale-105 transition"
      >
        <Sparkles className="w-5 h-5" />
        <span className="text-sm font-semibold">
          Ask Olivia
        </span>
        {!data.is_complete && (
          <span className="bg-white/20 text-xs font-mono px-2 py-0.5 rounded-full">
            {data.progress.completed}/{data.progress.total}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      data-testid="olivia-panel"
      className="fixed bottom-6 right-6 z-40 w-80 sm:w-96 bg-gradient-to-br from-slate-900 to-slate-950 border border-blue-500/30 rounded-2xl shadow-2xl shadow-blue-500/10 overflow-hidden flex flex-col"
      style={{ maxHeight: 'calc(100vh - 48px)' }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500/20 via-purple-500/15 to-blue-500/20 px-5 py-4 flex items-center justify-between border-b border-slate-800">
        <div className="flex items-center gap-2">
          {view === 'chat' ? (
            <button
              onClick={() => setView('guide')}
              data-testid="olivia-chat-back"
              className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800/50 mr-1"
              aria-label="Back to guide"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          )}
          <div>
            <div className="font-bold text-white" data-testid="olivia-title">
              {view === 'chat' ? 'Chat with Olivia' : 'Olivia'}
            </div>
            <div className="text-xs text-slate-400">
              {view === 'chat' ? 'AI guide · grounded in your data' : 'Your AI guide'}
            </div>
          </div>
        </div>
        <button
          onClick={toggleCollapsed}
          data-testid="olivia-close-btn"
          className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800/50"
          aria-label="Collapse Olivia"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {view === 'guide' && (
        <>
          {/* Progress bar */}
          <div className="px-5 pt-4">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-slate-400">Onboarding progress</span>
              <span className="font-mono text-blue-400" data-testid="olivia-progress-pct">
                {data.progress.percent}%
              </span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-500"
                style={{ width: `${data.progress.percent}%` }}
              />
            </div>
          </div>

          {/* Current step */}
          <div className="px-5 py-4 border-b border-slate-800">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Next step</div>
            <h3 className="text-lg font-bold text-white mb-1" data-testid="olivia-step-title">
              {data.title}
            </h3>
            <p className="text-sm text-slate-400 mb-3" data-testid="olivia-step-description">
              {data.description}
            </p>

            {data.rationale && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-xs text-blue-200">
                <Lightbulb className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>{data.rationale}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mt-4">
              <button
                onClick={() => navigate(data.action)}
                data-testid="olivia-continue-btn"
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90 text-white font-semibold text-sm transition"
              >
                {data.action_label || 'Continue'}
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setView('chat')}
                data-testid="olivia-open-chat-btn"
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm transition border border-slate-700"
              >
                <MessageCircle className="w-4 h-4" />
                Ask Olivia
              </button>
            </div>
          </div>

          {/* Step list */}
          <div className="px-5 py-3 max-h-56 overflow-y-auto" data-testid="olivia-steps-list">
            {steps.map((s) => {
              const isCurrent = s.step === data.step;
              return (
                <div
                  key={s.step}
                  data-testid={`olivia-step-${s.step}`}
                  className={`flex items-center gap-3 py-2 ${
                    isCurrent ? 'opacity-100' : s.completed ? 'opacity-60' : 'opacity-40'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    s.completed ? 'bg-emerald-500 text-white' :
                    isCurrent ? 'bg-blue-500 text-white animate-pulse' :
                    'bg-slate-800 text-slate-500'
                  }`}>
                    {s.completed ? <Check className="w-3 h-3" /> : '•'}
                  </div>
                  <span className={`text-xs ${isCurrent ? 'text-white font-semibold' : 'text-slate-400'}`}>
                    {s.title}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <button
            onClick={load}
            data-testid="olivia-refresh-btn"
            className="w-full px-5 py-2.5 text-xs text-slate-500 hover:text-slate-300 border-t border-slate-800 transition flex items-center justify-center gap-1.5"
          >
            <Loader2 className="w-3 h-3" /> Refresh status
          </button>
        </>
      )}

      {view === 'chat' && (
        <>
          {/* Messages */}
          <div
            ref={chatScrollRef}
            data-testid="olivia-chat-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            style={{ maxHeight: '400px' }}
          >
            {chatMessages.map((m, idx) => (
              <div
                key={idx}
                data-testid={`olivia-chat-msg-${m.role}`}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-blue-500 text-white rounded-br-sm'
                      : m.error
                        ? 'bg-red-500/10 border border-red-500/30 text-red-200 rounded-bl-sm'
                        : 'bg-slate-800 text-slate-100 rounded-bl-sm'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {chatSending && (
              <div className="flex justify-start">
                <div className="bg-slate-800 text-slate-400 px-3 py-2 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Olivia is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-800 p-3 flex gap-2">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
              placeholder="Ask Olivia anything…"
              disabled={chatSending}
              data-testid="olivia-chat-input"
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
            <button
              onClick={sendChatMessage}
              disabled={!chatInput.trim() || chatSending}
              data-testid="olivia-chat-send"
              className="px-3 rounded-lg bg-blue-500 hover:bg-blue-400 disabled:bg-slate-800 disabled:text-slate-500 text-white transition"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
